/**
 * RUBYN navbar menu tree — ported 1:1 from the old BoardV2 `rubynMenus`.
 * Sections drive the left links; each section's submenus drive the secondary
 * row; a submenu with `columns`/`image` opens the mega-menu screen.
 *
 * The tree holds **message ids**, never copy: the label for `nav.fashion.jeans`
 * lives in `messages/<locale>.json` alongside every other string. That keeps
 * the routing shape (canonical urls, image sources, ordering) in code and the
 * words in the catalog, which is the only split that survives ten languages.
 */

export interface MegaLink {
  textId: string;
  canonical: string;
  bold?: boolean;
}
export interface MegaColumn {
  type: "column";
  titleId?: string;
  links: MegaLink[];
}
export interface MegaImage {
  type: "image";
  textId: string;
  canonical: string;
  src: string;
}
export type MegaBlock = MegaColumn | MegaImage;

export interface SubMenu {
  titleId: string;
  canonical?: string;
  blocks?: MegaBlock[];
}
export interface Section {
  titleId: string;
  /** Top-level route the section links to (when redirectable). */
  canonical?: string;
  submenus: Record<string, SubMenu>;
}

export const NAV_SECTIONS: Record<string, Section> = {
  beauty: {
    titleId: "nav.sections.beauty",
    canonical: "/beauty",
    submenus: {},
  },
  fashion: {
    titleId: "nav.sections.fashion",
    canonical: "/fashion",
    submenus: {
      new: { titleId: "nav.fashion.new", canonical: "/fashion?sort=new" },
      clothing: { titleId: "nav.fashion.clothing", canonical: "/fashion/clothing" },
      dresses: { titleId: "nav.fashion.dresses", canonical: "/fashion/dresses" },
      sets: { titleId: "nav.fashion.sets", canonical: "/fashion/sets" },
      tops: { titleId: "nav.fashion.tops", canonical: "/fashion/tops" },
      jeans: { titleId: "nav.fashion.jeans", canonical: "/fashion/jeans" },
      jackets: { titleId: "nav.fashion.jackets", canonical: "/fashion/jackets" },
      sweaters: { titleId: "nav.fashion.sweaters", canonical: "/fashion/sweaters" },
      shoes: { titleId: "nav.fashion.shoes", canonical: "/fashion/shoes" },
      bottoms: { titleId: "nav.fashion.bottoms", canonical: "/fashion/bottoms" },
      jumpsuits: { titleId: "nav.fashion.jumpsuits", canonical: "/fashion/jumpsuits" },
      lingerie: {
        titleId: "nav.fashion.lingerie",
        blocks: [
          {
            type: "column",
            links: [
              { textId: "nav.lingerie.shopAll", canonical: "/fashion/lingerie", bold: true },
              { textId: "nav.lingerie.newIn", canonical: "/fashion/lingerie", bold: true },
              { textId: "nav.lingerie.backInStock", canonical: "/fashion/lingerie", bold: true },
              { textId: "nav.lingerie.lingerieDeals", canonical: "/fashion/lingerie", bold: true },
              { textId: "nav.lingerie.sleepDeals", canonical: "/fashion/lingerie", bold: true },
              { textId: "nav.lingerie.saleLingerie", canonical: "/fashion/lingerie", bold: true },
            ],
          },
          {
            type: "column",
            titleId: "nav.lingerie.sleepwear",
            links: [
              { textId: "nav.lingerie.shopAllSleepwear", canonical: "/fashion/lingerie" },
              { textId: "nav.lingerie.newInSleepwear", canonical: "/fashion/lingerie" },
              { textId: "nav.lingerie.holidayPajamas", canonical: "/fashion/lingerie" },
              { textId: "nav.lingerie.pajamaSets", canonical: "/fashion/lingerie" },
              { textId: "nav.lingerie.pajamaJumpsuits", canonical: "/fashion/lingerie" },
            ],
          },
          {
            type: "column",
            titleId: "nav.lingerie.sexy",
            links: [
              { textId: "nav.lingerie.shopAllLingerie", canonical: "/fashion/lingerie" },
              { textId: "nav.lingerie.lingerieSets", canonical: "/fashion/lingerie" },
              { textId: "nav.lingerie.babydolls", canonical: "/fashion/lingerie" },
              { textId: "nav.lingerie.teddies", canonical: "/fashion/lingerie" },
              { textId: "nav.lingerie.bodystockings", canonical: "/fashion/lingerie" },
              { textId: "nav.lingerie.blackLingerie", canonical: "/fashion/lingerie" },
            ],
          },
          {
            type: "column",
            titleId: "nav.lingerie.everyday",
            links: [
              { textId: "nav.lingerie.shopAllIntimates", canonical: "/fashion/lingerie" },
              { textId: "nav.lingerie.panties", canonical: "/fashion/lingerie" },
              { textId: "nav.lingerie.shapewear", canonical: "/fashion/lingerie" },
              { textId: "nav.lingerie.underwireBras", canonical: "/fashion/lingerie" },
              { textId: "nav.lingerie.wirelessBras", canonical: "/fashion/lingerie" },
              { textId: "nav.lingerie.thongs", canonical: "/fashion/lingerie" },
            ],
          },
          {
            type: "image",
            textId: "nav.lingerie.shopLingerie",
            canonical: "/fashion/lingerie",
            src: "https://cdn.shopify.com/s/files/1/0293/9277/files/11-18-25_S5_9_ZDFXY532_Nudecombo_ZSR_TK_PC_11-13-53_42458_PXF.jpg",
          },
        ],
      },
      accessories: { titleId: "nav.fashion.accessories", canonical: "/fashion/accessories" },
      loungewear: { titleId: "nav.fashion.loungewear", canonical: "/fashion/loungewear" },
    },
  },
  about: {
    titleId: "nav.sections.about",
    canonical: "/about-us",
    submenus: {
      company: { titleId: "nav.about.company", canonical: "/about-us" },
      careers: { titleId: "nav.about.careers", canonical: "/careers" },
      stores: { titleId: "nav.about.stores", canonical: "/stores" },
      campaigns: { titleId: "nav.about.campaigns", canonical: "/campaigns/hervoice" },
    },
  },
};

export const REDIRECT_SECTIONS = ["beauty", "fashion"];
export const DEFAULT_SECTION = "fashion";

/**
 * Section slugs that get a catalog route (`/[locale]/fashion`, `/…/beauty`).
 *
 * A section qualifies when its canonical is exactly its own slug: `beauty` →
 * `/beauty`, `fashion` → `/fashion`. `about` does not — it points at
 * `/about-us`, an editorial page that already exists and holds no products.
 * Derived rather than hardcoded, so adding a browsable section to the nav gives
 * it a catalog page with no extra wiring.
 */
export function sectionSlugs(): string[] {
  return Object.entries(NAV_SECTIONS)
    .filter(([key, def]) => def.canonical === `/${key}`)
    .map(([key]) => key);
}

/**
 * `{section, category}` pairs that get their own URL.
 *
 * Collected from every two-segment `canonical` the nav can reach — a submenu's
 * own link *and* the links inside a mega-menu, because a submenu like Lingerie
 * has no canonical of its own and is only reachable through its blocks. Miss
 * those and the mega-menu links 404.
 *
 * `/fashion?sort=new` is excluded on purpose: it is a sort, not a category.
 *
 * Deeper categories (the backend exposes ~140) are reachable through the
 * sidebar as `?category=<slug>`; giving each one a prerendered page would cost
 * a thousand HTML files in the Capacitor bundle to save one query parameter.
 */
export function sectionCategoryPairs(): { section: string; category: string }[] {
  const seen = new Set<string>();
  const pairs: { section: string; category: string }[] = [];

  const consider = (section: string, canonical?: string) => {
    const segments = canonical?.split("?")[0]?.split("/").filter(Boolean) ?? [];
    if (segments.length !== 2 || segments[0] !== section) return;
    const key = `${section}/${segments[1]}`;
    if (seen.has(key)) return;
    seen.add(key);
    pairs.push({ section, category: segments[1] });
  };

  for (const section of sectionSlugs()) {
    for (const sub of Object.values(NAV_SECTIONS[section].submenus)) {
      consider(section, sub.canonical);
      for (const block of sub.blocks ?? []) {
        if (block.type === "image") consider(section, block.canonical);
        else for (const link of block.links) consider(section, link.canonical);
      }
    }
  }
  return pairs;
}

/**
 * The submenu a `/section/category` URL belongs to, so the page can title
 * itself with the same label the nav uses.
 *
 * Matches a submenu's own canonical first, then the links inside its
 * mega-menu — Lingerie is reachable only through the latter.
 */
export function findCategorySubmenu(section: string, category: string): SubMenu | null {
  const def = NAV_SECTIONS[section];
  if (!def) return null;
  const target = `/${section}/${category}`;
  const matches = (canonical?: string) => canonical?.split("?")[0] === target;

  for (const sub of Object.values(def.submenus)) {
    if (matches(sub.canonical)) return sub;
    for (const block of sub.blocks ?? []) {
      if (block.type === "image" ? matches(block.canonical) : block.links.some((l) => matches(l.canonical))) {
        return sub;
      }
    }
  }
  return null;
}
